
import React, { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { useModal, DateField } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useDataRefresh, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { ENTGatewayDocumentService } from 'ent-capability-gateway-document';
import config from 'app-config';
import { TranslatorContext } from '@jutro/locale';
import { Button, InputField, DropdownSelectField } from '@jutro/legacy/components';
import { Link } from '@jutro/router';
import messages from './DocumentUploadComponent.messages.js';
import metadata from './DocumentUploadComponent.metadata.json5';
import styles from './DocumentUploadComponent.module.scss';


function DocumentUploadComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showAlert } = useModal();
    const { policyNumber, productCode, selectedDocs, setSelectedDocs, history, cancellationStarted, docTypeValues = [], formattedCommonDocTypeCodes = [], defaultDocType, setParentLoading
    } = props;
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState();
    const [documents, setDocuments] = useState(!_.isEmpty(selectedDocs) ? selectedDocs : []);
    const [displayDocuments, setDisplayDocuments] = useState(!_.isEmpty(selectedDocs) ? selectedDocs : []);
    const [loading, setLoading] = useState(false);
    const translator = useContext(TranslatorContext);
    const { twiaPhoneNumber } = config.phoneNumberConfig;
    const [buildings] = useState([{
        code: 0,
        name: 'N/A'
    }]);

    useEffect(() => {
        setDocumentTypes(docTypeValues);
        setDocumentType(defaultDocType);
    }, []);

    useEffect(() => {
        if (cancellationStarted.isStarted) {
            if (_.isEmpty(documents)) {
                return history.push(`/cancellation/${cancellationStarted.jobNumber}/summary`, cancellationStarted.effectiveDateResponse);
            }
            const documentMetaDataList = _.map(documents, (doc, index) => ({
                docUID: `${index + 1}`.padStart(3, '0'),
                documentType: _.get(doc, 'documentType'),
                securityType: 'unrestricted',
                status: 'approved',
                jobNumber: _.get(cancellationStarted, 'jobNumber'),
                policyNumber,
                name: _.get(doc, 'name'),
                sessionID: '-',
                skipReviewActivity_TWIA: true,
                description: _.get(doc, 'description'),
                ...(!_.isNil(_.get(doc, 'buildingFixedID_TWIA')) && {
                    buildingFixedID_TWIA: _.get(doc, 'buildingFixedID_TWIA')
                })
            }));

            setLoading(true);
            setParentLoading(true);
            Promise.all(
                _.map(documents, (doc, index) =>
                    ENTGatewayDocumentService.uploadDocument(
                        doc,
                        documentMetaDataList[index],
                        authHeader
                    )
                )
            )
                .catch(() => {
                    showAlert({
                        title: commonMessages.errorUploadTitle,
                        message: translator(messages.uploadFailedMessage, {
                            uwCompanyName: "TWIA",
                            contactNumber: twiaPhoneNumber,
                        }),
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok,
                    }).catch(_.noop);
                })
                .finally(() => {
                    setLoading(false);
                    setParentLoading(false);
                    history.push(`/cancellation/${cancellationStarted.jobNumber}/summary`, cancellationStarted.effectiveDateResponse);
                });
        }
    }, [cancellationStarted, documents]);

    const onUploadDocument = useCallback(item => {
        const docList = [...documents];
        _.set(item, 'documentType', 'cancellation_doc_ext');
        _.set(item, 'dateModified', LocalDateUtil.today());
        if (productCode !== 'Homeowners') {
            _.set(item, 'buildingFixedID_TWIA', 0); // set N/A by default
        }
        docList.push(item);
        setDisplayDocuments(docList);
        setDocuments(docList);
        setSelectedDocs(docList);
    }, [documents]);

    const onChangeDocumentType = useCallback(
        docType => {
            setDocumentType(docType);
        },
        [setDocumentType]
    );

    const onDownloadDocument = useCallback(async item => {
        setLoading(true);

        if (policyNumber) {
            _.set(item, 'policyNumber', policyNumber);
        }

        try {
            const blobUrl = window.URL.createObjectURL(item);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            showAlert({
                title: commonMessages.errorUploadTitle,
                message: translator(messages.unableToDownload),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok,
            })
                .catch(_.noop);
        } finally {
            setLoading(false);
        }
    }, [policyNumber, showAlert]);

    const getDocumentName = useCallback((item, index) => (
        <Link style={{ justifyItems: 'left' }} onClick={() => { onDownloadDocument(item) }}>{item.name}</Link>
    ), [onDownloadDocument]);

    const getDocumentType = (item) => {
        const code = formattedCommonDocTypeCodes?.find(val => val.code == item.documentType);
        return code ? code.name : item.documentType;
    };

    const writeDocumentValue = useCallback((index, path, value) => {
        const doc = displayDocuments[index];
        _.set(doc, path, value);
        doc.edited = true;
        const updatedDocuments = _.cloneDeep(displayDocuments);
        updatedDocuments[index] = doc;
        setDocuments(updatedDocuments);
        setDisplayDocuments(updatedDocuments);
        setSelectedDocs(updatedDocuments);
        refreshData();
    }, [displayDocuments, setDocuments, setDisplayDocuments, refreshData]);

    const getDocumentDescription = useCallback((item, index) => {
        return (
            <InputField
                id={`description${index}`}
                path="description"
                onValueChange={(value) => { writeDocumentValue(index, 'description', value) }}
                value={item.description}
            />
        )
    }, [writeDocumentValue]);

    const getDocumentBuilding = useCallback((item, index) => {
        return (
            <DropdownSelectField
                id={`building${index}`}
                alwaysShowPlaceholder={false}
                availableValues={buildings}
                path="buildingFixedID_TWIA"
                onValueChange={(value) => { writeDocumentValue(index, 'buildingFixedID_TWIA', value) }}
                value={item.buildingFixedID_TWIA}
            />
        )
    }, [writeDocumentValue]);

    const getDocumentUploaded = useCallback((item, index) => (
        <DateField
            id={`uploadedDate${index}`}
            value={item.dateModified}
            readOnly
            displayFormat="vshort"
        />
    ), []);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        documentUploadContainer: {
            visible: !loading
        },
        loadingIndicator: {
            visible: loading
        },
        uploadDocumentType: {
            availableValues: documentTypes,
            value: documentType
        },
        uploadDocument: {
            disabled: _.isUndefined(documentType)
        },
        documentsTable: {
            data: displayDocuments
        },
        buildingColumn: {
            visible: !_.isUndefined(productCode) && productCode !== 'Homeowners',
        },
        uploadInstructionsRes: {
            visible: !_.isUndefined(productCode) && productCode === 'Homeowners',
        },
        uploadInstructionsComm: {
            visible: !_.isUndefined(productCode) && productCode === 'CommercialProperty',
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {},
        resolveCallbackMap: {
            onUploadDocument,
            onChangeDocumentType,
            getDocumentName,
            getDocumentDescription,
            getDocumentType,
            getDocumentBuilding,
            getDocumentUploaded
        },
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            overrideProps={overrideProps}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

export default DocumentUploadComponent;
